import { MoneyFieldsFragment } from "@repo/graphql/documents/generic/fragment.generated";
import { MontuCampaignBudgetTypeChoices } from "@repo/graphql/gql/graphql";

import { formatMoneyFromFragment, formatMoneySymbolOnly } from "./index";

export type CampaignBudget =
  | { kind: "fixed"; amount: MoneyFieldsFragment }
  | { kind: "range"; min: MoneyFieldsFragment; max: MoneyFieldsFragment }
  | { kind: "quote" }
  | { kind: "none" };

export interface CampaignBudgetSource {
  budgetType: { enum: MontuCampaignBudgetTypeChoices };
  minBudget: MoneyFieldsFragment;
  maxBudget: MoneyFieldsFragment;
}

/**
 * What a campaign pays. Mirrors `CampaignBudget.from_columns` in the backend
 * (`montu/strategy/campaign/budget.py`) — keep the two in sync. The rule, in
 * order, comparing amounts as numbers:
 *
 * 1. `budgetType` is QUOTE -> quote (the amounts are ignored).
 * 1b. `budgetType` is OFF -> none (the amounts are ignored).
 * 2. `maxBudget <= 0` -> none.
 * 3. `minBudget == maxBudget` -> fixed.
 * 4. otherwise -> range. The backend offers creators `maxBudget`.
 */
export const campaignBudget = (
  campaign: CampaignBudgetSource,
): CampaignBudget => {
  if (campaign.budgetType.enum === MontuCampaignBudgetTypeChoices.Quote) {
    return { kind: "quote" };
  }
  if (campaign.budgetType.enum === MontuCampaignBudgetTypeChoices.Off) {
    return { kind: "none" };
  }
  if (campaign.maxBudget.amount <= 0) return { kind: "none" };
  if (campaign.minBudget.amount === campaign.maxBudget.amount) {
    return { kind: "fixed", amount: campaign.maxBudget };
  }
  return { kind: "range", min: campaign.minBudget, max: campaign.maxBudget };
};

/** The amount creators are offered: the fixed amount, or the most a range
 *  pays (the backend's `CampaignBudget.amount`); null for quote / none. */
export const offeredAmount = (
  budget: CampaignBudget,
): MoneyFieldsFragment | null => {
  switch (budget.kind) {
    case "fixed":
      return budget.amount;
    case "range":
      return budget.max;
    case "quote":
    case "none":
      return null;
  }
};

/** "$40.000" for a fixed budget, "$40.000 – $60.000" for a range; null when the
 *  campaign has no cash amount to show (quote / none). */
export const formatCampaignBudget = (budget: CampaignBudget): string | null => {
  switch (budget.kind) {
    case "fixed":
      return formatMoneyFromFragment(budget.amount);
    case "range":
      return `${formatMoneySymbolOnly(budget.min)} – ${formatMoneyFromFragment(budget.max)}`;
    case "quote":
    case "none":
      return null;
  }
};

/** The amount a creator's application starts with: the amount they are offered
 *  (the fixed amount, or the most a range pays), otherwise 0 (they type their
 *  own quote). */
export const initialOfferAmount = (
  budget: CampaignBudget,
  currency: string,
): { amount: string; currency: string } => {
  switch (budget.kind) {
    case "fixed":
      return {
        amount: String(budget.amount.amount),
        currency: budget.amount.currency.code,
      };
    case "range":
      return {
        amount: String(budget.max.amount),
        currency: budget.max.currency.code,
      };
    case "quote":
    case "none":
      return { amount: "0", currency };
  }
};
